/**
 * Job name: what a drafted job is called in the rental system.
 *
 * A row of dropdowns, each one part of the name, joined with " - ". The
 * saved value is the template go-service/internal/jobname renders, so
 * "Customer (att: Contact)", "Job type", "Dates" is
 * "{customer} {att} - {type} - {dates}". No parts is "", and the rental
 * system names the job itself, which is what every Company had before.
 *
 * A Company with no name set starts from four parts, JOB_NAME_DEFAULT,
 * rather than an empty row. They are a draft until the row is first
 * touched: a pick, a removal, or focus leaving the row saves them.
 *
 * Only the last part can be removed, so there is one × on the row and
 * the parts never shuffle under the reader.
 *
 * It commits itself (DESIGN.md, Save model): a pick or a removal saves at
 * once, and a part just added waits until something is picked in it.
 *
 * A template set by HireOS that is not parts joined by " - " (Crown Events'
 * "{date:DD.MM.YY} equipment hire") is not shown as dropdowns. It reads as
 * a custom format, and adding a part replaces it.
 */
import { Select } from "../../components/ui/Select";
import { Tooltip } from "../../components/ui/Tooltip";
import { InlineBanner } from "../../components/ui/InlineBanner";
import { PlusIcon, XIcon } from "../../components/ui/icons";
import {
  getCompanySettings,
  saveCompanySettings,
} from "../../lib/settings-api";
import { useSettingCommit } from "../../lib/use-setting-commit";
import { useSettingsSource } from "../../lib/use-settings-source";
import { FieldLabel } from "./section-chrome";
import { useEffect, useState } from "react";
import styles from "./JobNameRow.module.css";

/** Each part a dropdown offers, in the order it offers them. */
export const JOB_NAME_PARTS: { value: string; label: string }[] = [
  { value: "{customer}", label: "Customer" },
  { value: "{customer} {att}", label: "Customer (att: Contact)" },
  { value: "{org}", label: "Organisation" },
  { value: "{contact}", label: "Contact" },
  { value: "{type}", label: "Job type" },
  { value: "{event}", label: "Event name" },
  { value: "{venue}", label: "Venue" },
  { value: "{dates}", label: "Dates" },
  { value: "{date}", label: "First day" },
  { value: "{setup}", label: "Setup day" },
  { value: "{dh}", label: "DH" },
];

export const MAX_JOB_NAME_PARTS = 6;

/** Where a Company with no name set starts. */
export const JOB_NAME_DEFAULT = ["{customer} {att}", "{type}", "{dates}", "{dh}"];
const SEPARATOR = " - ";

/**
 * The parts a saved format is made of, or null where it is not parts
 * joined by " - " that the dropdowns offer.
 */
export function parseJobNameFormat(format: string): string[] | null {
  if (format.trim() === "") return [];
  const parts = format.split(SEPARATOR);
  if (parts.length > MAX_JOB_NAME_PARTS) return null;
  return parts.every((p) => JOB_NAME_PARTS.some((o) => o.value === p))
    ? parts
    : null;
}

const sameParts = (a: string[] | null, b: string[] | null) =>
  a === b ||
  (a !== null && b !== null && a.length === b.length && a.every((p, i) => p === b[i]));

/** A part added with + and not yet picked. */
const UNPICKED = "";

export function JobNameRow({ systemName }: { systemName: string }) {
  const source = useSettingsSource(getCompanySettings);
  const loaded = source.data !== null;
  const savedFormat = source.data?.job_name_format ?? "";

  const field = useSettingCommit<string[] | null>(
    parseJobNameFormat(savedFormat),
    async (parts) => {
      const next = await saveCompanySettings({
        job_name_format: (parts ?? []).join(SEPARATOR),
      });
      source.setData(next);
    },
    { equals: sameParts },
  );

  // Nothing set yet: start from the default parts, once, when the
  // settings arrive. Removing every part later is still "".
  const [started, setStarted] = useState(false);
  const { setDraft } = field;
  useEffect(() => {
    if (!loaded || started) return;
    setStarted(true);
    if (savedFormat === "") setDraft(JOB_NAME_DEFAULT);
  }, [loaded, started, savedFormat, setDraft]);

  const custom = field.draft === null;
  const parts = field.draft ?? [];

  /** Show the edit, and save it once every part has something picked. */
  const change = (next: string[]) => {
    field.setDraft(next);
    field.setError(null);
    if (next.includes(UNPICKED)) return;
    if (next.join(SEPARATOR) === savedFormat) return;
    void field.commit(next);
  };

  return (
    <div
      className={styles.row}
      onBlur={(e) => {
        // Only when focus leaves the row: moving into a dropdown's options
        // is still editing it.
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        if (!parts.includes(UNPICKED)) void field.commitDraft();
      }}
    >
      <FieldLabel
        htmlFor="job-name-part-1"
        tooltip={`The title HireOS gives each job it creates in ${systemName}.`}
      >
        Job name
      </FieldLabel>
      <p className={styles.note}>
        With no fields, {systemName} names the job.
      </p>

      {custom && (
        <p className={styles.note}>
          A custom format set by HireOS. Add a field to replace it.
        </p>
      )}

      <div className={styles.parts} role="group" aria-label="Job name fields">
        {parts.map((part, i) => (
          <span key={i} className={styles.partWrap}>
            {i > 0 && <span className={styles.separator} aria-hidden>-</span>}
            <span className={styles.part}>
              <Select
                id={`job-name-part-${i + 1}`}
                size="sm"
                ariaLabel={`Field ${i + 1}`}
                value={part}
                options={JOB_NAME_PARTS}
                disabled={!loaded || field.saving}
                onChange={(v) => change(parts.map((p, j) => (j === i ? v : p)))}
              />
              {i === parts.length - 1 && (
                <Tooltip content="Remove field from title" trigger="button">
                  <button
                    type="button"
                    className={styles.remove}
                    aria-label="Remove field from title"
                    disabled={!loaded || field.saving}
                    onClick={() => change(parts.slice(0, -1))}
                  >
                    <XIcon className={styles.icon} />
                  </button>
                </Tooltip>
              )}
            </span>
          </span>
        ))}
        {parts.length < MAX_JOB_NAME_PARTS && (
          <Tooltip content="Add new field to title" trigger="button">
            <button
              type="button"
              className={styles.add}
              aria-label="Add new field to title"
              disabled={!loaded || field.saving}
              onClick={() => change([...parts, UNPICKED])}
            >
              <PlusIcon className={styles.icon} />
            </button>
          </Tooltip>
        )}
      </div>

      {(field.error || source.loadError) && (
        <InlineBanner variant="error">{field.error ?? source.loadError}</InlineBanner>
      )}
    </div>
  );
}
